import type { ColumnFiltersState } from '@tanstack/react-table'
import type { CardFilterValues } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/types'
import { isBrowseFilterId } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllFilters'
import {
  hasNonTextValueChange,
  hasTextValueChange,
  TEXT_FILTER_COMMIT_DEBOUNCE_MS,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseFilterCommit'
import type {
  SearchHistoryItem,
  SearchStateUpdate,
} from 'features/knowledgeDocumentViewer/types'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export function cardFiltersToSyncedColumnFilters(
  cardFilters: CardFilterValues
): ColumnFiltersState {
  return Object.entries(cardFilters)
    .filter(
      ([id, v]) =>
        isBrowseFilterId(id) &&
        ((typeof v === 'string' && v.length > 0) ||
          (Array.isArray(v) && v.length > 0))
    )
    .map(([id, value]) => ({ id, value }))
}

export function columnFiltersToCardFilters(
  filters: ColumnFiltersState | undefined
): CardFilterValues {
  const out: CardFilterValues = {}
  if (!filters?.length) return out
  for (const { id, value } of filters) {
    if (!isBrowseFilterId(id)) continue
    if (typeof value === 'string' && value.length > 0) {
      out[id] = value
    } else if (
      Array.isArray(value) &&
      value.every((item) => typeof item === 'string')
    ) {
      if (value.length > 0) out[id] = value
    }
  }
  return out
}

type OnSearchStateChange = (updates: SearchStateUpdate) => void

export function useDocumentSearchResultFilters({
  onSearchStateChange,
  isServerPaged = false,
  searchStateFilters,
}: {
  onSearchStateChange: OnSearchStateChange
  isServerPaged?: boolean
  searchStateFilters: SearchHistoryItem['searchState']['filters']
}) {
  const [localFilters, setLocalFiltersState] = useState<ColumnFiltersState>(
    () => searchStateFilters ?? []
  )
  const localFiltersRef = useRef(localFilters)
  localFiltersRef.current = localFilters
  const pendingFiltersRef = useRef<ColumnFiltersState | undefined>(undefined)

  const setLocalFilters = useCallback((next: ColumnFiltersState) => {
    localFiltersRef.current = next
    setLocalFiltersState(next)
  }, [])

  const cardFilters = useMemo(
    () => columnFiltersToCardFilters(localFilters),
    [localFilters]
  )
  const columnFiltersStateKey = JSON.stringify(searchStateFilters ?? [])

  useEffect(() => {
    const committed = searchStateFilters ?? []
    if (
      pendingFiltersRef.current !== undefined &&
      !isEqual(pendingFiltersRef.current, committed)
    ) {
      return
    }
    pendingFiltersRef.current = undefined
    setLocalFilters(committed)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- columnFiltersStateKey is the canonical content key
  }, [columnFiltersStateKey, setLocalFilters])

  const commitColumnFilters = useCallback(
    (filters: ColumnFiltersState) => {
      pendingFiltersRef.current = filters
      onSearchStateChange((current) => ({
        filters,
        ...(isServerPaged
          ? { pagination: { ...current.pagination, pageIndex: 0 } }
          : {}),
      }))
    },
    [onSearchStateChange, isServerPaged]
  )

  const debouncedCommitColumnFilters = useMemo(
    () => debounce(commitColumnFilters, TEXT_FILTER_COMMIT_DEBOUNCE_MS),
    [commitColumnFilters]
  )
  useEffect(
    () => () => debouncedCommitColumnFilters.cancel(),
    [debouncedCommitColumnFilters]
  )

  const cancelPendingCommit = useCallback(() => {
    debouncedCommitColumnFilters.cancel()
    pendingFiltersRef.current = undefined
  }, [debouncedCommitColumnFilters])

  const scheduleCommit = useCallback(
    (next: ColumnFiltersState) => {
      const prev = searchStateFilters ?? []
      if (isEqual(next, prev)) {
        cancelPendingCommit()
        return
      }
      pendingFiltersRef.current = next
      if (
        hasTextValueChange(prev, next) &&
        !hasNonTextValueChange(prev, next)
      ) {
        debouncedCommitColumnFilters(next)
        return
      }
      cancelPendingCommit()
      commitColumnFilters(next)
    },
    [
      cancelPendingCommit,
      commitColumnFilters,
      debouncedCommitColumnFilters,
      searchStateFilters,
    ]
  )

  const handleCardFiltersEdit = useCallback(
    (next: CardFilterValues) => {
      const synced = cardFiltersToSyncedColumnFilters(next)
      setLocalFilters(synced)
      if (isServerPaged) {
        scheduleCommit(synced)
      }
    },
    [isServerPaged, scheduleCommit, setLocalFilters]
  )

  const commitCardFiltersToUrl = useCallback(() => {
    const synced = localFiltersRef.current
    const prev = searchStateFilters ?? []
    cancelPendingCommit()
    if (isEqual(synced, prev)) return
    commitColumnFilters(synced)
  }, [cancelPendingCommit, commitColumnFilters, searchStateFilters])

  const handleTableFiltersChange = useCallback(
    (
      updater:
        | ColumnFiltersState
        | ((prev: ColumnFiltersState) => ColumnFiltersState)
    ) => {
      const prev = localFiltersRef.current
      const next = typeof updater === 'function' ? updater(prev) : updater
      setLocalFilters(next)
      if (isServerPaged) {
        scheduleCommit(next)
        return
      }
      if (!isEqual(next, searchStateFilters ?? [])) {
        onSearchStateChange({ filters: next })
      }
    },
    [
      isServerPaged,
      onSearchStateChange,
      scheduleCommit,
      searchStateFilters,
      setLocalFilters,
    ]
  )

  return {
    cardFilters,
    commitCardFiltersToUrl,
    handleCardFiltersEdit,
    handleTableFiltersChange,
    tableFilters: localFilters,
  }
}
